import React from 'react';
import { DateTime } from 'luxon';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import {
  isCrmTicketTerminalWorkflowStatus,
  resolveCrmTicketListWorkflowStatus,
  resolveCrmTicketStepLineDisplay,
} from '../../../utils/crmCommunicationTicketStatus';

function awaitingUserDisplayName(user) {
  return user?.employee?.name || user?.username || '';
}

export function CrmTicketStepLineCell({ stepLine }) {
  if (!stepLine?.label) return null;
  if (stepLine.mode === 'pill') {
    return (
      <span
        className="inline-flex max-w-full items-center rounded-md px-2 py-1 text-sm font-normal text-white whitespace-nowrap overflow-hidden text-ellipsis w-fit"
        style={{ backgroundColor: stepLine.color || '#9ca3af' }}
        title={stepLine.label}
      >
        {stepLine.label}
      </span>
    );
  }
  return (
    <span className="block min-w-0 truncate text-sm text-gray-700" title={stepLine.label}>
      {stepLine.label}
    </span>
  );
}

export function CrmTicketListStatusCell({ rowData, crmStates = [], locale = 'sk', showStepLine = false }) {
  const display = resolveCrmTicketListWorkflowStatus(rowData, crmStates, locale);
  const stepLine = showStepLine ? resolveCrmTicketStepLineDisplay(rowData, crmStates, locale) : null;

  if (!display?.name) {
    const fallback = rowData?.workflow_state_label ? String(rowData.workflow_state_label) : '';
    if (!fallback && !stepLine?.label) return null;
    return (
      <div className="w-full overflow-hidden flex flex-col gap-0.5">
        {fallback ? (
          <span
            className="inline-flex max-w-full items-center rounded-md px-2 py-1 text-base font-normal bg-gray-400 text-white whitespace-nowrap overflow-hidden text-ellipsis w-fit"
            title={fallback}
          >
            {fallback}
          </span>
        ) : null}
        <CrmTicketStepLineCell stepLine={stepLine} />
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden flex flex-col gap-0.5">
      <span
        className="inline-flex max-w-full items-center rounded-md px-2 py-1 text-base font-normal text-white whitespace-nowrap overflow-hidden text-ellipsis w-fit"
        style={{ backgroundColor: display.color || '#9ca3af' }}
        title={display.name}
      >
        {display.name}
      </span>
      <CrmTicketStepLineCell stepLine={stepLine} />
    </div>
  );
}

export function CrmTicketListDeadlineCell({
  rowData,
  crmStates = [],
  dateFmt,
  getUserInitials,
  getUserColor,
  timezone = 'Europe/Bratislava',
}) {
  const users = Array.isArray(rowData?.awaiting_users)
    ? rowData.awaiting_users
    : (Array.isArray(rowData?.awaiting_user) ? rowData.awaiting_user : []);
  const maxVisible = 3;
  const visibleUsers = users.slice(0, maxVisible);
  const extraUsers = users.slice(maxVisible);
  const extraCount = extraUsers.length;
  const extraNames = extraUsers.map(awaitingUserDisplayName).filter(Boolean);

  const deadlineIso = rowData?.deadline ?? rowData?.waiting_for_action_deadline_at;
  const deadlineDt = deadlineIso ? DateTime.fromISO(String(deadlineIso)) : null;
  const deadlineText = deadlineDt?.isValid ? deadlineDt.toFormat(dateFmt || 'dd.MM.yyyy') : '';
  const isClosedStatus = isCrmTicketTerminalWorkflowStatus(rowData, crmStates);
  const today = DateTime.now().setZone(timezone).startOf('day');
  const isExpired = deadlineDt?.isValid && deadlineDt < today;

  return (
    <div className="flex items-center justify-start w-full h-full min-w-0 flex-wrap gap-1">
      {!isClosedStatus && users.length > 0 ? (
        <AvatarGroup>
          {visibleUsers.map((user) => {
            const name = awaitingUserDisplayName(user);
            return (
              <Avatar
                key={user.id}
                title={name}
                label={getUserInitials?.(name)}
                shape="circle"
                style={{
                  backgroundColor: getUserColor?.(user?.username) || '#6b7280',
                  color: '#ffffff',
                  marginRight: '5px',
                }}
              />
            );
          })}
          {extraCount > 0 ? (
            <Avatar
              label={`+${extraCount}`}
              shape="circle"
              title={extraNames.join('\n')}
              style={{ backgroundColor: '#6b7280', color: '#ffffff', marginRight: '5px' }}
            />
          ) : null}
        </AvatarGroup>
      ) : null}
      {deadlineDt?.isValid && !isClosedStatus ? (
        <div
          className={
            isExpired
              ? 'text-red-500 font-semibold shrink-0'
              : 'shrink-0'
          }
        >
          {deadlineText}
        </div>
      ) : null}
    </div>
  );
}
